import { useFeatureGate } from '@statsig/expo-bindings';
import React from 'react';
import { isNonMobileWeb } from 'smoothie';

import { useUserAuthorization } from '../../auth/hooks/useUserAuthorization';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import type { ContainerComponent } from '../../common/types/Common';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import { captureException } from '../../logger/Logger';
import { GuideMessagingModal } from '../components/GuideMessagingModal';
import useGetGuideMessaging from '../hooks/useGetGuideMessaging';
import { GuideMessagingContext } from '../hooks/useGuideMessaging';
import SendbirdProvider from './SendbirdProvider';

const GuideMessagingProvider: ContainerComponent = ({ children }) => {
    const user = useAuthedUser();
    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);
    const { accessToRestrictedCapabilities } = useUserAuthorization();

    // Under GOYO, don't load guide messaging for members with no guide yet —
    // there is no messaging account to fetch, so the request 422s and logs a
    // spurious "Failed to load Guide Messaging." (Non-GOYO behavior unchanged.)
    const hasGuide = user?.membership?.guide != null;
    const guideMessagingEnabled =
        user != null &&
        accessToRestrictedCapabilities.guideMessaging === true &&
        (!goyoEnabled || hasGuide);

    // Fetch the user's Sendbird credentials.
    const { data: guideMessaging, isLoading: isLoadingGuideMessaging } =
        useGetGuideMessaging(
            { userId: user?.id ?? '' },
            {
                enabled: guideMessagingEnabled,
            },
        );

    if (!accessToRestrictedCapabilities.guideMessaging) {
        return <>{children}</>;
    }

    if (guideMessagingEnabled && !isLoadingGuideMessaging && !guideMessaging) {
        captureException(new Error(`Failed to load Guide Messaging.`));
    }

    return (
        <GuideMessagingContext.Provider
            value={{
                guideMessaging: guideMessaging ?? null,
                isLoading: isLoadingGuideMessaging,
            }}
        >
            <SendbirdProvider>
                {children}
                {isNonMobileWeb && <GuideMessagingModal />}
            </SendbirdProvider>
        </GuideMessagingContext.Provider>
    );
};

export default GuideMessagingProvider;
